import { useEffect, useMemo, useState } from "react"
import { ArrowRight } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { normalizePhone } from "@/lib/utils/phone"
import { withShopName } from "@/lib/intake/intake-config"
import type { PublicShopProfile } from "@/lib/api/public-consumer"
import { AInput, EMAIL_RE, focusFirstInvalid } from "@/components/onboarding-activation/ui"
import { FlowCta, FlowHeading, IntakeField, PhasePane, QuietLink } from "./flow-chrome"
import type { IntakeDevice } from "./intake-flow-types"
import { CONSUMER_V2 } from "./copy"

// Contact-first — the lead is created the moment this screen submits, so a
// consumer who drops at photos is still a lead the shop can chase. Name +
// phone always required; email per shop config. Everything validates live
// after the first submit attempt; the CTA sits in-flow (keyboard-safe).

const C = CONSUMER_V2.contact

export interface ContactValue {
  name: string
  phone: string
  email: string
  smsOptIn: boolean
}

// ".con" is the one typo worth catching — it passes EMAIL_RE and silently
// kills every follow-up email.
function emailTypoFix(email: string): string | null {
  const m = email.trim().match(/^(.+@[^\s@]+)\.con$/i)
  return m ? `${m[1]}.com` : null
}

export function StepContactFirst({
  shop,
  device,
  customer,
  setCustomer,
  resumed,
  hasTrackingRef,
  onTrack,
  onStartFresh,
  saving,
  error,
  onSubmit,
}: {
  shop: PublicShopProfile
  device: IntakeDevice
  customer: ContactValue
  setCustomer: (patch: Partial<ContactValue>) => void
  resumed: boolean
  hasTrackingRef: boolean
  onTrack: () => void
  onStartFresh: () => void
  saving: boolean
  error: string | null
  onSubmit: (customer: { name: string; phone: string; email: string; smsOptIn: boolean }) => void
}) {
  const [submitted, setSubmitted] = useState(false)
  const emailRequired = shop.intake.required.email

  // Honor the shop's configured opt-in default exactly once, on a fresh form.
  useEffect(() => {
    if (!resumed && !shop.intake.consent.smsOptIn && customer.smsOptIn) {
      setCustomer({ smsOptIn: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const first = customer.name.trim().split(/\s+/)[0] || ""
  const nameOk = customer.name.trim().length >= 2
  const phone = useMemo(() => normalizePhone(customer.phone), [customer.phone])
  const emailTrimmed = customer.email.trim()
  const emailOk = emailRequired
    ? EMAIL_RE.test(emailTrimmed)
    : emailTrimmed.length === 0 || EMAIL_RE.test(emailTrimmed)
  const typoFix = emailTypoFix(emailTrimmed)
  const valid = nameOk && phone.valid && emailOk

  const showErr = (ok: boolean) => submitted && !ok

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSubmitted(true)
    if (!valid) {
      focusFirstInvalid()
      return
    }
    onSubmit({
      name: customer.name.trim(),
      phone: phone.display,
      email: emailTrimmed,
      smsOptIn: customer.smsOptIn,
    })
  }

  return (
    <PhasePane className={cx(device === "desktop" && "mx-auto w-full max-w-[480px]")}>
      {hasTrackingRef && (
        <button
          type="button"
          onClick={onTrack}
          className="mt-4 flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-signal/8 px-4 py-3 text-left transition-colors hover:bg-signal/12 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        >
          <span className="text-[14px] font-semibold text-ink">{C.trackingBanner}</span>
          <span className="inline-flex shrink-0 items-center gap-1 text-[14px] font-semibold text-emerald-800">
            {C.trackIt}
            <ArrowRight className="size-4" aria-hidden />
          </span>
        </button>
      )}

      <header className="mt-6">
        {resumed && first ? (
          <>
            <FlowHeading className="text-[30px] font-bold leading-[1.12] tracking-tight text-ink md:text-[34px]">
              {C.welcomeBack(first)}
            </FlowHeading>
            <p className="mt-2.5 text-[16px] leading-relaxed text-zinc-600">
              Pick up where you left off. Your details are still here.
            </p>
          </>
        ) : (
          <>
            <FlowHeading className="text-[30px] font-bold leading-[1.12] tracking-tight text-ink md:text-[34px]">
              {shop.intake.welcome.title}
            </FlowHeading>
            <p className="mt-2.5 text-[16px] leading-relaxed text-zinc-600">
              {withShopName(shop.intake.welcome.message, shop.name)}
            </p>
          </>
        )}
      </header>

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
        <IntakeField label={C.nameLabel} error={showErr(nameOk) && C.errors.name}>
          {(control) => (
            <AInput
              {...control}
              value={customer.name}
              onChange={(e) => setCustomer({ name: e.target.value })}
              placeholder="Jane Doe"
              maxLength={120}
              autoComplete="name"
              autoCapitalize="words"
              valid={nameOk}
              invalid={showErr(nameOk)}
              className="h-13 rounded-xl text-[17px]"
            />
          )}
        </IntakeField>

        <IntakeField label={C.phoneLabel} hint={C.phoneHint} error={showErr(phone.valid) && C.errors.phone}>
          {(control) => (
            <AInput
              {...control}
              type="tel"
              inputMode="tel"
              value={customer.phone}
              onChange={(e) => setCustomer({ phone: normalizePhone(e.target.value).display })}
              placeholder="(555) 123-4567"
              maxLength={20}
              autoComplete="tel"
              valid={phone.valid}
              invalid={showErr(phone.valid)}
              className="h-13 rounded-xl text-[17px]"
            />
          )}
        </IntakeField>

        <IntakeField
          label={emailRequired ? C.emailLabel : C.emailOptionalLabel}
          error={showErr(emailOk) && C.errors.email}
        >
          {(control) => (
            <>
              <AInput
                {...control}
                type="email"
                inputMode="email"
                value={customer.email}
                onChange={(e) => setCustomer({ email: e.target.value })}
                placeholder="jane@example.com"
                maxLength={254}
                autoComplete="email"
                autoCapitalize="none"
                valid={emailTrimmed.length > 0 && EMAIL_RE.test(emailTrimmed) && !typoFix}
                invalid={showErr(emailOk)}
                className="h-13 rounded-xl text-[17px]"
              />
              {typoFix && (
                <button
                  type="button"
                  onClick={() => setCustomer({ email: typoFix })}
                  className="mt-1 min-h-11 text-[14px] font-semibold text-emerald-800 underline decoration-emerald-800/30 underline-offset-4 hover:decoration-emerald-800"
                >
                  {C.emailTypoHint(typoFix)}
                </button>
              )}
            </>
          )}
        </IntakeField>

        {error && (
          <p role="alert" className="rounded-xl bg-amber-50 px-4 py-3 text-[14px] font-medium text-amber-900">
            {error}
          </p>
        )}

        <div className="pt-1">
          <FlowCta type="submit" loading={saving}>
            {saving ? C.ctaBusy : C.cta}
            {!saving && <ArrowRight className="size-4" aria-hidden />}
          </FlowCta>
        </div>

        {/* Consent + SMS opt-in — sent with the create call. */}
        <label className="flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-xl px-1">
          <span className="text-[14px] font-medium text-zinc-700">{C.smsOptIn}</span>
          <button
            type="button"
            role="switch"
            aria-checked={customer.smsOptIn}
            onClick={() => setCustomer({ smsOptIn: !customer.smsOptIn })}
            className={cx(
              "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
              customer.smsOptIn ? "bg-signal" : "bg-zinc-300",
            )}
          >
            <span
              className={cx(
                "absolute top-0.5 size-6 rounded-full bg-white shadow transition-[left] duration-150",
                customer.smsOptIn ? "left-[22px]" : "left-0.5",
              )}
            />
          </button>
        </label>
        <p className="text-[13px] leading-relaxed text-zinc-500">
          {withShopName(shop.intake.consent.text, shop.name)}
        </p>

        {device === "desktop" && (
          <p className="text-[14px] leading-relaxed text-zinc-600">
            Next, you take the photos with your phone. No phone handy? You can upload them from this computer.
          </p>
        )}
      </form>

      {resumed && first && (
        <QuietLink onClick={onStartFresh} className="mt-2">
          {C.startFresh(first)}
        </QuietLink>
      )}
    </PhasePane>
  )
}
